import { Mail, MapPin, Phone } from "lucide-react";
import { normalizeSiteContent } from "../utils/siteContent";

export default function ContactPage({ content }) {
  const safeContent = normalizeSiteContent(content);
  const phoneHref = `tel:${safeContent.contact.phone.replace(/\s+/g, "")}`;

  return (
    <div className="page-shell section-gap" data-testid="contact-page">
      <section className="space-y-10" data-testid="contact-grid-section">
        <div className="max-w-4xl space-y-4">
          <p className="eyebrow" data-testid="contact-eyebrow">
            Contact the firm
          </p>
          <h1 className="headline-lg max-w-3xl" data-testid="contact-heading">
            Reach the office directly by phone, email, or visit details.
          </h1>
          <p className="body-copy max-w-3xl" data-testid="contact-intro-copy">
            This website is now intentionally simple and direct: no submission flow, no waiting screen, just the core ways to get in touch with the firm.
          </p>
        </div>

        <div className="grid gap-5 lg:grid-cols-3">
          <article className="panel-card bg-slate-950 text-white" data-testid="contact-phone-card">
            <Phone className="h-10 w-10 text-amber-400" />
            <p className="mt-6 text-xs font-bold uppercase tracking-[0.26em] text-amber-400" data-testid="contact-phone-heading">
              Telephone
            </p>
            <a className="mt-5 block text-2xl font-black uppercase tracking-tight text-white" data-testid="contact-phone-link" href={phoneHref}>
              {safeContent.contact.phone}
            </a>
            <p className="mt-4 text-sm leading-relaxed text-slate-300" data-testid="contact-fax-text">
              Fax: {safeContent.contact.fax}
            </p>
          </article>

          <article className="panel-card" data-testid="contact-email-card">
            <Mail className="h-10 w-10 text-amber-700" />
            <p className="mt-6 text-xs font-bold uppercase tracking-[0.26em] text-amber-700" data-testid="contact-email-heading">
              Email
            </p>
            <div className="mt-5 space-y-3">
              {safeContent.contact.emails.map((email) => (
                <a className="block text-base font-semibold text-slate-950 transition-colors duration-300 hover:text-amber-700" data-testid={`contact-email-link-${email.replace(/[@.]/g, "-")}`} href={`mailto:${email}`} key={email}>
                  {email}
                </a>
              ))}
            </div>
          </article>

          <article className="panel-card bg-slate-50" data-testid="contact-address-card">
            <MapPin className="h-10 w-10 text-amber-700" />
            <p className="mt-6 text-xs font-bold uppercase tracking-[0.26em] text-amber-700" data-testid="contact-address-heading">
              Office location
            </p>
            <div className="mt-5 space-y-2">
              {safeContent.contact.address_lines.map((line, index) => (
                <p className="text-base text-slate-800" data-testid={`contact-address-line-${index + 1}`} key={line}>
                  {line}
                </p>
              ))}
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-600" data-testid="contact-region-note">
              {safeContent.contact.office_region}
            </p>
          </article>
        </div>

        <div className="border border-slate-900/10 bg-white p-8 md:p-10" data-testid="contact-direct-note">
          <p className="text-xs font-bold uppercase tracking-[0.26em] text-amber-700" data-testid="contact-direct-note-heading">
            Direct appointments
          </p>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-700" data-testid="contact-direct-note-copy">
            {safeContent.contact.consultation_note}
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <a className="solid-button" data-testid="contact-call-button" href={phoneHref}>
              Call the office
            </a>
            <a className="outline-button" data-testid="contact-email-button" href={`mailto:${safeContent.contact.emails[0]}`}>
              Email the firm
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}